import React, { useState } from "react";
import { graphql, useFragment, useLazyLoadQuery } from "react-relay";
import { LazyQueryNetworkBoundrary } from "../../../../../components/mobile/QueryNetworkBoundrary";
import { WATeamScheduleSettingsQuery } from "./__generated__/WATeamScheduleSettingsQuery.graphql";
import { WATeamScheduleSettings_teamDialog$key } from "./__generated__/WATeamScheduleSettings_teamDialog.graphql";
import {
  AHIcon,
  AHSeparator,
  Box,
  Circle,
  Dialog,
  ListItem,
  ListItemGroup,
  SectionHeader,
  useBoxStyle,
} from "arkh";
import { FlatList, ScrollView } from "react-native";
import { TeamIconCircle } from "../../../../../components/mobile/TeamIconCircle";
import { useWATeamScheduleContext } from "./WATeamScheduleContext";
import { FormattedMessage, useIntl } from "react-intl";
import { useSizeClass } from "../../../../../common/Utils/useSizeClass";
import { SizeClass } from "@/src/common/Utils/SizeClass";
import { WAFakeHeader } from "../../WidgetAdd/WAFakeHeader";
import { useIntlTokens } from "../../../../../components/mobile/useIntlTokens";

export function WATeamScheduleSettings(props: { onClose(): void }) {
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {(fetchKey) => (
        <InnerWATeamScheduleSettings
          fetchKey={fetchKey}
          onClose={props.onClose}
        />
      )}
    </LazyQueryNetworkBoundrary>
  );
}

function InnerWATeamScheduleSettings(props: {
  fetchKey: string;
  onClose(): void;
}) {
  const intl_tokens = useIntlTokens();
  const intl = useIntl();
  const context = useWATeamScheduleContext();
  const isCompact = useSizeClass().width === SizeClass.COMPACT;

  const data = useLazyLoadQuery<WATeamScheduleSettingsQuery>(
    graphql`
      query WATeamScheduleSettingsQuery {
        store {
          publicTeams {
            id
            name
            roles {
              id
              name
            }
            ...TeamIconCircleComponent_Team
            ...WATeamScheduleSettings_teamDialog
          }
        }
      }
    `,
    {},
    {
      fetchKey: props.fetchKey,
      fetchPolicy: "store-and-network",
    }
  );

  const [teamDialogVisible, setTeamDialogVisible] = useState(false);
  const [rolesDialogVisible, setRolesDialogVisible] = useState(false);

  const selectedTeam = context.teamId
    ? data.store.publicTeams.find((t) => t.id === context.teamId)
    : null;

  const selectedTeamHasRoles = (selectedTeam?.roles.length ?? 0) > 0;

  const contentContainerStyle = useBoxStyle({
    spaceHorizontal: "medium",
    spaceVertical: "medium",
  });

  const selectedNames: string[] = [];
  if (context.withoutRoleSelected) {
    selectedNames.push(intl_tokens.WithoutRole);
  }
  if (selectedTeam) {
    for (const role of selectedTeam.roles) {
      if (context.roleIds.includes(role.id)) {
        selectedNames.push(role.name);
      }
    }
  }

  const rolesLabel =
    selectedNames.length === 0
      ? intl_tokens.Roles
      : selectedNames.length === 2
        ? selectedNames.join(" & ")
        : selectedNames.join(", ");

  return (
    <Box style={{ flex: 1 }}>
      {!isCompact ? (
        <WAFakeHeader
          title={intl.formatMessage({
            defaultMessage: "Settings",
            description: "WATeamScheduleSettings header title",
          })}
        />
      ) : null}
      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={contentContainerStyle}
      >
        <SectionHeader>
          <SectionHeader.Title>
            <FormattedMessage
              defaultMessage="Filters"
              description="WATeamScheduleSettings Filters section title"
            />
          </SectionHeader.Title>
        </SectionHeader>
        <ListItemGroup>
          <ListItem
            isFirst
            isLast={!selectedTeamHasRoles}
            border="border-primary"
            onPress={() => setTeamDialogVisible(true)}
            left={
              selectedTeam ? (
                <TeamIconCircle team={selectedTeam} size="smallMedium" />
              ) : (
                <Circle size="smallMedium">
                  <AHIcon type="team" size="small" color="text-secondary" />
                </Circle>
              )
            }
            right={<ListItem.NavArrow />}
          >
            <ListItem.Title>
              {selectedTeam?.name ?? intl_tokens.Team}
            </ListItem.Title>
          </ListItem>
          {selectedTeamHasRoles ? (
            <>
              <AHSeparator />
              <ListItem
                isLast
                border="border-primary"
                onPress={() => setRolesDialogVisible(true)}
                left={
                  <Circle size="smallMedium">
                    <AHIcon
                      type="employee-role"
                      size="small"
                      color="text-secondary"
                    />
                  </Circle>
                }
                right={<ListItem.NavArrow />}
              >
                <ListItem.Title>{rolesLabel}</ListItem.Title>
              </ListItem>
            </>
          ) : null}
        </ListItemGroup>

        <Box spaceTop="medium" />
        <SectionHeader>
          <SectionHeader.Title>
            <FormattedMessage
              defaultMessage="View"
              description="WATeamScheduleSettings View section title"
            />
          </SectionHeader.Title>
        </SectionHeader>
        <ListItemGroup>
          <ListItem
            isFirst
            border="border-primary"
            onPress={() => context.setContentType("DAILY_WITH_SUMMARY")}
            rightEnabled={context.contentType === "DAILY_WITH_SUMMARY"}
            right={<ListItem.Check />}
          >
            <ListItem.Title>
              <FormattedMessage
                defaultMessage="Routines summary"
                description="WATeamScheduleSettings Daily With Summary Routines option title"
              />
            </ListItem.Title>
            <ListItem.SubTitle>
              <FormattedMessage
                defaultMessage="Presents an overview of weekly and monthly routines, along with live daily routines."
                description="WATeamScheduleSettings Daily With Summary Routines option subtitle"
              />
            </ListItem.SubTitle>
          </ListItem>
          <AHSeparator />
          <ListItem
            border="border-primary"
            onPress={() => context.setContentType("DAILY")}
            rightEnabled={context.contentType === "DAILY"}
            right={<ListItem.Check />}
          >
            <ListItem.Title>{intl_tokens.DailyRoutines}</ListItem.Title>
          </ListItem>
          <AHSeparator />
          <ListItem
            border="border-primary"
            onPress={() => context.setContentType("WEEKLY")}
            rightEnabled={context.contentType === "WEEKLY"}
            right={<ListItem.Check />}
          >
            <ListItem.Title>{intl_tokens.WeeklyRoutines}</ListItem.Title>
          </ListItem>
          <AHSeparator />
          <ListItem
            isLast
            border="border-primary"
            onPress={() => context.setContentType("MONTHLY")}
            rightEnabled={context.contentType === "MONTHLY"}
            right={<ListItem.Check />}
          >
            <ListItem.Title>{intl_tokens.MonthlyRoutines}</ListItem.Title>
          </ListItem>
        </ListItemGroup>
      </ScrollView>

      <TeamSelectDialog
        teams={data.store.publicTeams}
        visible={teamDialogVisible}
        onClose={() => setTeamDialogVisible(false)}
      />

      {selectedTeam ? (
        <RoleSelectDialog
          roles={selectedTeam.roles}
          visible={rolesDialogVisible}
          onClose={() => setRolesDialogVisible(false)}
        />
      ) : null}
    </Box>
  );
}

function TeamSelectDialog(props: {
  teams: WATeamScheduleSettings_teamDialog$key;
  visible: boolean;
  onClose(): void;
}) {
  const intl_tokens = useIntlTokens();
  const context = useWATeamScheduleContext();
  const teams = useFragment(
    graphql`
      fragment WATeamScheduleSettings_teamDialog on Team @relay(plural: true) {
        id
        name
        isPrivate
        ...TeamIconCircleComponent_Team
      }
    `,
    props.teams
  );
  const boxStyle = useBoxStyle({ space: "medium" });

  return (
    <Dialog
      onClose={props.onClose}
      visible={props.visible}
      minHeight={400}
      title={intl_tokens.Team}
    >
      <FlatList
        ItemSeparatorComponent={AHSeparator}
        data={teams}
        renderItem={({ item, index }) => (
          <ListItem
            isFirst={index === 0}
            isLast={index === teams.length - 1}
            left={<TeamIconCircle team={item} size="smallMedium" />}
            border="border-primary"
            right={
              item.isPrivate ? null : (
                <ListItem.SelectIndicator
                  selected={context.teamId === item.id}
                />
              )
            }
            onPress={() => {
              context.setTeamId(item.id);
              props.onClose();
            }}
            disabled={item.isPrivate}
          >
            <ListItem.Title>{item.name}</ListItem.Title>
          </ListItem>
        )}
        keyExtractor={(item) => item.id}
        contentContainerStyle={boxStyle}
      />
    </Dialog>
  );
}

function RoleSelectDialog(props: {
  roles: readonly { readonly id: string; readonly name: string }[];
  visible: boolean;
  onClose(): void;
}) {
  const intl_tokens = useIntlTokens();
  const context = useWATeamScheduleContext();
  const boxStyle = useBoxStyle({ space: "medium" });

  const onPressRole = (roleId: string) => {
    if (context.roleIds.includes(roleId)) {
      context.setRoleIds(context.roleIds.filter((id) => id !== roleId));
    } else {
      context.setRoleIds([...context.roleIds, roleId]);
    }
  };

  return (
    <Dialog
      onClose={props.onClose}
      visible={props.visible}
      minHeight={400}
      title={intl_tokens.Roles}
    >
      <ScrollView contentContainerStyle={boxStyle}>
        <ListItem
          isFirst
          isLast={props.roles.length === 0}
          border="border-primary"
          onPress={() =>
            context.setWithoutRoleSelected(!context.withoutRoleSelected)
          }
          right={
            <ListItem.SelectIndicator selected={context.withoutRoleSelected} />
          }
        >
          <ListItem.Title>{intl_tokens.WithoutRole}</ListItem.Title>
        </ListItem>
        {props.roles.map((role, index) => (
          <React.Fragment key={role.id}>
            <AHSeparator />
            <ListItem
              isLast={index === props.roles.length - 1}
              border="border-primary"
              onPress={() => onPressRole(role.id)}
              right={
                <ListItem.SelectIndicator
                  selected={context.roleIds.includes(role.id)}
                />
              }
            >
              <ListItem.Title>{role.name}</ListItem.Title>
            </ListItem>
          </React.Fragment>
        ))}
      </ScrollView>
    </Dialog>
  );
}
